import { useEffect, useState, useRef } from "react";

/**
 * Tracks the browser's online/offline status with a small "stable" debounce
 * to avoid flickering banners on intermittent micro-disconnects.
 *
 * Also returns a `lastReconnectAt` timestamp (incrementing) consumers can
 * use as a useEffect dependency to immediately refresh their data when the
 * connection comes back.
 *
 * @returns {{ isOnline: boolean, lastReconnectAt: number }}
 */
export default function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator !== "undefined" ? navigator.onLine : true
  );
  const [lastReconnectAt, setLastReconnectAt] = useState(0);
  const offlineTimerRef = useRef(null);

  useEffect(() => {
    const goOnline = () => {
      if (offlineTimerRef.current) {
        clearTimeout(offlineTimerRef.current);
        offlineTimerRef.current = null;
      }
      setIsOnline(true);
      setLastReconnectAt(Date.now());
    };
    const goOffline = () => {
      // 1.5 s debounce: brief disconnects (e.g. wifi roaming) don't trigger
      // a visible banner — only sustained loss does.
      if (offlineTimerRef.current) return;
      offlineTimerRef.current = setTimeout(() => {
        setIsOnline(false);
        offlineTimerRef.current = null;
      }, 1500);
    };
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      if (offlineTimerRef.current) clearTimeout(offlineTimerRef.current);
    };
  }, []);

  return { isOnline, lastReconnectAt };
}
